import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import {
  usePagination,
  usePageOffset,
  DEFAULT_PAGE_SIZE,
} from "@/hooks/use-pagination";
import { useFilterSearchParams } from "@/hooks/use-filter-search-params";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState, ErrorState, Text } from "@/components/ui/text";
import { FilterChip } from "@/components/ui/filter-chip";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { Icon } from "@/components/ui/icons";
import { PageLoadingOverlay } from "@/components/ui/page-loading-overlay";
import {
  SortableHeaderCell,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { ResizeHandle } from "@/components/ui/resize-handle";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { TableViewport } from "@/components/ui/table-viewport";
import {
  ResizableTableShell,
  type ColumnDef,
} from "@/components/ui/resizable-table-shell";
import { InteractiveCard } from "@/components/ui/interactive-card";
import { CompactCardSkeleton } from "@/components/ui/compact-card-skeleton";
import { StickyHeaderSlot } from "@/components/ui/sticky-header-slot";
import { VStack } from "@/components/ui/v-stack";
import { HStack } from "@/components/ui/h-stack";
import { DividedList } from "@/components/ui/divided-list";
import { useSizeClass } from "@/hooks/use-size-class";
import { useSortableTable } from "@/hooks/use-sortable-table";
import { getAdminEntities } from "@/lib/api/admin/entities";
import type { AdminEntity } from "@/lib/api/admin/types";
import { formatAdminDate } from "@/components/admin/formatting";
import { fromIntlLanguage } from "@/lib/i18n/config";
import { getCurrentUser } from "@/lib/api/auth";
import { highlight } from "@/lib/highlight";
import { entitySearchableFields } from "@/lib/search/entity";

type EntityTypeFilter =
  | "All"
  | "Shipper"
  | "TransportCompany"
  | "Broker"
  | "Driver";

const EMPTY_ENTITIES: AdminEntity[] = [];

function AdminEntitiesPage() {
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";
  const isRegular = sizeClass === "regular";
  const navigate = useNavigate();
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tAdminEntities } = useTranslation("admin.entities");
  const { t: tAdminEntityType } = useTranslation(
    "admin.applications.entityType",
  );
  const { t: tCommon } = useTranslation("common");

  const { filters, setFilter } = useFilterSearchParams({
    keys: ["search", "entity_type"],
    defaults: { entity_type: "All" },
  });
  const searchQuery = filters.search;
  const entityFilter = filters.entity_type as EntityTypeFilter;

  const { offset } = usePageOffset();

  // FilterToolbar already debounces draft → URL commits at 300ms, so
  // searchQuery here is the committed (debounced) value.
  const q = useMemo(() => {
    const trimmed = searchQuery.trim();
    return trimmed.length >= 3 ? trimmed : undefined;
  }, [searchQuery]);

  const entitiesQuery = useQuery({
    queryKey: [
      ...queryKeys.admin.entities({ limit: DEFAULT_PAGE_SIZE, offset }),
      q,
    ],
    queryFn: () => getAdminEntities({ limit: DEFAULT_PAGE_SIZE, offset, q }),
  });

  const entities = entitiesQuery.data?.data ?? EMPTY_ENTITIES;
  const total = entitiesQuery.data?.total ?? 0;
  const loading = entitiesQuery.isLoading;
  const errorKind = entitiesQuery.isError ? ("load" as const) : null;

  const { page, totalPages, setPage } = usePagination({ total });

  const filteredEntities = useMemo(() => {
    // Search narrows server-side via ?q=; entity_type filter remains client-side.
    if (entityFilter === "All") return entities;
    return entities.filter((e) => e.type === entityFilter);
  }, [entities, entityFilter]);

  const {
    sort,
    toggleSort,
    sortedData: sortedEntities,
  } = useSortableTable(filteredEntities, { defaultColumn: "name" });

  const entityOptions = [
    { value: "All", label: tAdminEntityType("all") },
    { value: "Shipper", label: tAdminEntityType("shipper") },
    {
      value: "TransportCompany",
      label: tAdminEntityType("carrier"),
    },
    { value: "Broker", label: tAdminEntityType("broker") },
    { value: "Driver", label: tAdminEntityType("driver") },
  ];

  // ─── Resizable columns ──────────────────────────────────────────────
  const currentUser = useMemo(() => getCurrentUser(), []);
  const columnDefs = useMemo((): ColumnDef[] => {
    const allCols: ColumnDef[] = [
      { key: "name", defaultWidth: 180, minWidth: 100 },
      { key: "type", defaultWidth: 140, minWidth: 80 },
      { key: "city", defaultWidth: 140, minWidth: 80 },
      { key: "status", defaultWidth: 120, minWidth: 80 },
      { key: "created", defaultWidth: 160, minWidth: 100 },
      { key: "actions", defaultWidth: 120, minWidth: 80 },
    ];
    if (isRegular) return allCols.filter((c) => c.key !== "created");
    return allCols;
  }, [isRegular]);

  return (
    <VStack gap="lg" data-testid="admin-entities-page">
      <StickyHeaderSlot>
        <Card variant="bordered" padding="xs">
          <FilterToolbar
            searchPlaceholder={tCommon("search")}
            searchValue={searchQuery}
            onSearchChange={(v) => setFilter("search", v)}
            pagination={{
              page,
              totalPages,
              perPage: DEFAULT_PAGE_SIZE,
              onPageChange: setPage,
              loading,
            }}
            quickFilters={
              <>
                {entityOptions.map((opt) => (
                  <FilterChip
                    key={opt.value}
                    active={entityFilter === opt.value}
                    onClick={() => setFilter("entity_type", opt.value)}
                    data-testid={`admin-entities-type-${opt.value}`}
                  >
                    {opt.label}
                  </FilterChip>
                ))}
              </>
            }
            data-testid="admin-entities-toolbar"
          />
        </Card>
      </StickyHeaderSlot>

      <Card padding="none" data-testid="admin-entities-card">
        <TableViewport position="relative">
          {loading && entities.length === 0 ? (
            isCompact ? (
              <CompactCardSkeleton />
            ) : (
              <TableSkeleton columns={6} rows={5} />
            )
          ) : errorKind ? (
            <ErrorState
              title={tAdminEntities("failedToLoad")}
              action={
                <Button
                  color="neutral"
                  variant="outline"
                  onClick={() => void entitiesQuery.refetch()}
                >
                  {tCommon("retry")}
                </Button>
              }
              className="py-16"
            />
          ) : sortedEntities.length === 0 ? (
            <EmptyState
              icon={<Icon.Building size="xl" />}
              title={tAdminEntities("title")}
              description={tAdminEntities("noEntitiesFound")}
              className="py-16"
            />
          ) : isCompact ? (
            <DividedList>
              {sortedEntities.map((entity) => {
                const fields = entitySearchableFields(entity, language);
                return (
                  <InteractiveCard
                    key={entity.id}
                    data-testid={`entity-card-${entity.id}`}
                    onClick={() =>
                      void navigate({ to: `/admin/entities/${entity.id}` })
                    }
                  >
                    <HStack justify="between" gap="none">
                      <Text weight="semibold">
                        {highlight(fields.name, searchQuery)}
                      </Text>
                      <Text
                        size="xs"
                        color="muted"
                        className={
                          entity.is_approved
                            ? "text-[color:var(--accent-green)]"
                            : "text-[color:var(--accent-yellow)]"
                        }
                      >
                        {entity.is_approved
                          ? tAdminEntities("approved")
                          : tAdminEntities("pending")}
                      </Text>
                    </HStack>
                    <Text size="xs" color="muted">
                      {highlight(fields.type, searchQuery)}
                      {fields.city.value ? (
                        <> &middot; {highlight(fields.city, searchQuery)}</>
                      ) : (
                        ""
                      )}
                    </Text>
                  </InteractiveCard>
                );
              })}
            </DividedList>
          ) : (
            <ResizableTableShell
              tableId="admin-entities"
              userId={currentUser?.id ?? null}
              columns={columnDefs}
            >
              {({ onResizeStart }) => (
                <>
                  <TableHead>
                    <TableRow>
                      <SortableHeaderCell
                        column="name"
                        sortColumn={sort?.column}
                        sortDirection={sort?.direction}
                        onSort={toggleSort}
                        className="relative"
                      >
                        {tAdminEntities("columns.name")}
                        <ResizeHandle
                          columnKey="name"
                          onResizeStart={onResizeStart}
                        />
                      </SortableHeaderCell>
                      <SortableHeaderCell
                        column="type"
                        sortColumn={sort?.column}
                        sortDirection={sort?.direction}
                        onSort={toggleSort}
                        className="relative"
                      >
                        {tAdminEntities("columns.type")}
                        <ResizeHandle
                          columnKey="type"
                          onResizeStart={onResizeStart}
                        />
                      </SortableHeaderCell>
                      <TableHeaderCell className="relative">
                        {tAdminEntities("columns.city")}
                        <ResizeHandle
                          columnKey="city"
                          onResizeStart={onResizeStart}
                        />
                      </TableHeaderCell>
                      <SortableHeaderCell
                        column="is_approved"
                        sortColumn={sort?.column}
                        sortDirection={sort?.direction}
                        onSort={toggleSort}
                        className="relative"
                      >
                        {tAdminEntities("columns.status")}
                        <ResizeHandle
                          columnKey="status"
                          onResizeStart={onResizeStart}
                        />
                      </SortableHeaderCell>
                      {!isRegular && (
                        <TableHeaderCell className="relative">
                          {tAdminEntities("columns.created")}
                          <ResizeHandle
                            columnKey="created"
                            onResizeStart={onResizeStart}
                          />
                        </TableHeaderCell>
                      )}
                      <TableHeaderCell className="relative text-right" sticky>
                        {tAdminEntities("columns.actions")}
                        <ResizeHandle
                          columnKey="actions"
                          onResizeStart={onResizeStart}
                        />
                      </TableHeaderCell>
                      <TableHeaderCell className="w-auto" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedEntities.map((entity) => {
                      const fields = entitySearchableFields(entity, language);
                      return (
                        <TableRow key={entity.id}>
                          <TableCell className="font-medium">
                            {highlight(fields.name, searchQuery)}
                          </TableCell>
                          <TableCell>
                            {highlight(fields.type, searchQuery)}
                          </TableCell>
                          <TableCell>
                            {fields.city.value
                              ? highlight(fields.city, searchQuery)
                              : "—"}
                          </TableCell>
                          <TableCell>
                            <Text
                              className={
                                entity.is_approved
                                  ? "text-[color:var(--accent-green)]"
                                  : "text-[color:var(--accent-yellow)]"
                              }
                            >
                              {entity.is_approved
                                ? tAdminEntities("approved")
                                : tAdminEntities("pending")}
                            </Text>
                          </TableCell>
                          {!isRegular && (
                            <TableCell>
                              {formatAdminDate(entity.created_at, language)}
                            </TableCell>
                          )}
                          <TableCell className="text-right" sticky>
                            <Button
                              type="button"
                              color="neutral"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                void navigate({
                                  to: `/admin/entities/${entity.id}`,
                                })
                              }
                            >
                              {tCommon("view")}
                            </Button>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </>
              )}
            </ResizableTableShell>
          )}
          {loading && entities.length > 0 && (
            <PageLoadingOverlay
              page={page}
              label={tCommon("pagination.loadingPage")}
            />
          )}
        </TableViewport>
      </Card>
    </VStack>
  );
}

export const Route = createFileRoute("/_app/admin/entities/")({
  component: AdminEntitiesPage,
});
